import type { DrawingDTO, MonsterDTO, PersonalityId, SkillId } from '@shared/protocol/messages';
import type { ItemSlot, QuestMetric } from '../data/presets';
import type { LanguageSetting } from '../../i18n';

export const SAVE_VERSION = 1;

export interface StatAlloc {
  hp: number;
  atk: number;
  def: number;
  luck: number;
}

export interface Hero {
  id: string;
  name: string;
  classId: string;
  /** Player drawing; null = use the class doodle. */
  drawing: DrawingDTO | null;
  /** Ready-made hero worn instead of the drawing (shared/looks.ts); null/absent = the drawing or class doodle. */
  look?: string | null;
  level: number;
  exp: number;
  statPoints: number;
  alloc: StatAlloc;
  equipment: Partial<Record<ItemSlot, string>>;
  skill: SkillId;
  createdAt: number;
  /** Real losses in a row (systems/injury.ts); a win resets it. */
  lossStreak?: number;
  /** Resting after a losing streak until this time (ms); absent or past = ready. */
  injuredUntil?: number;
}

export interface Monster {
  id: string;
  name: string;
  hp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
  personality: PersonalityId;
  reward: { exp: number; coins: number };
  mode: MonsterDTO['mode'];
  tier: MonsterDTO['tier'];
  drawing: DrawingDTO | null;
  /** Atlas art id for built-in monsters. */
  preset?: string;
  /**
   * hall = a contest winner from the Hall of Fame (id `hall:<entry id>`),
   * dare = a friend's dare (id `dare:<dare id>`), boss = the boss of the week
   * (id `boss:<round>`).
   */
  source: 'preset' | 'mine' | 'shared' | 'hall' | 'dare' | 'boss' | 'tower' | 'board';
  author?: string;
  shareCode?: string;
  wins: number;
  losses: number;
  createdAt: number;
}

export interface Memory {
  id: string;
  at: number;
  kind: 'battle' | 'level' | 'quest' | 'create' | 'friend' | 'item';
  title: string;
  text: string;
  sticker?: 'star' | 'heart' | 'skull' | 'crown' | 'pencil';
}

export interface Friend {
  id: string;
  name: string;
  lastSeen: number;
  battles: number;
  wins: number;
  losses: number;
}

export interface Settings {
  volume: number;
  /** Background music volume 0–1 ("Mute sounds" silences it too). */
  music: number;
  muted: boolean;
  reducedMotion: 'auto' | 'on' | 'off';
  debug: boolean;
  fpsCap: 30 | 60;
  dprCap: number;
  /**
   * Who draws the game: 'cpu' (Canvas 2D, light on the graphics card; the
   * default) or 'gpu' (WebGL, smoother on strong machines). Applies on reload.
   */
  renderMode: 'cpu' | 'gpu';
  /** UI language; 'auto' follows the device. */
  language: LanguageSetting;
  /** Phones: the first tap enters full screen. */
  fullscreenOnPhone: boolean;
  /** Ink Strike trace mini-game after some won rounds (ui/traceGame.ts). */
  traceGame: boolean;
  /** Drawing tools: 'easy' (big brushes, bucket up front) or 'pro' (fine brushes, any colour). */
  drawMode: 'easy' | 'pro';
}

export type Counters = Record<Exclude<QuestMetric, 'level'>, number>;

export interface OnlineIdentity {
  playerId: string;
  name: string;
  token: string;
}

/** Age band picked on the first-start age screen (the birth year itself is not kept). */
export type AgeGroup = 'under13' | 'teen' | 'adult';

/** Stationery-shop cosmetics: they change looks only, never battle stats. */
export interface Cosmetics {
  /** Bought cosmetic ids (pens, victory stamps). */
  owned: string[];
  /** Victory stamp shown on the result card; null = the plain one. */
  stamp: string | null;
}

/** Daily quests: today's picks and the counters as they were when the day began. */
export interface DailyProgress {
  /** Local date "YYYY-MM-DD"; '' before the first day starts. */
  day: string;
  base: Counters;
  quests: string[];
  claimed: string[];
  /** Days in a row with all quests done, ending on lastFull. */
  streak: number;
  lastFull: string | null;
}

/** Monster contest progress kept on this device. */
export interface ContestProgress {
  /** Rounds whose first vote already paid its coins. */
  votedRounds: string[];
  /** Winning entries of mine whose champion prize was already given. */
  prizes: string[];
}

/** Paper fortune teller: free tickets earned by playing, and draws paid with coins (never real money). */
export interface FortuneProgress {
  /** Free draws the player can make now (capped, see data/fortune.ts). */
  tickets: number;
  /** Day ("YYYY-MM-DD") whose free ticket was already given; null = never. */
  lastFreeDay: string | null;
  draws: number;
  /** Draws in a row without a prize of that tier or better (the guarantees). */
  sinceRare: number;
  sinceEpic: number;
  sinceLegendary: number;
  /** Coin draws made on coinDay (capped per day). */
  coinDay: string;
  coinDraws: number;
}

/** Community progress kept on this device. */
export interface CommunityProgress {
  /** Boss weeks whose "we beat the boss" prize was already given. */
  bossPrizes: string[];
  /** The class I am in, as last seen on the server (battles are only reported for the class goal while set). */
  classId: string | null;
  /** Class weeks whose goal prize was already claimed. */
  classPrizes: string[];
  /** The notebook card friends last got (JSON), so it is only sent when it changes. */
  cardSent: string;
}

export interface SaveData {
  version: number;
  /** null until the player answers the age screen (kid mode is the safe default meanwhile). */
  ageGroup: AgeGroup | null;
  cosmetics: Cosmetics;
  contest: ContestProgress;
  community: CommunityProgress;
  daily: DailyProgress;
  fortune: FortuneProgress;
  /** Extra hero slots bought with coins (real-money ones are `heroslot:N` in cosmetics.owned). */
  heroSlots: number;
  heroes: Hero[];
  activeHeroId: string | null;
  /**
   * A battle that counts is running (written when it starts, cleared when it
   * ends). Still set when the game loads = the page was reloaded or closed
   * mid-fight, which counts as a loss (progression.forfeitUnfinished).
   */
  battleInProgress: { heroId: string; monsterId: string; monsterName: string; at: number } | null;
  /** Player-created and imported monsters (presets live in data/presets.ts). */
  monsters: Monster[];
  coins: number;
  items: string[];
  /** Upgrade level (+1..+5) of owned items; missing = +0. */
  itemLevels: Record<string, number>;
  /**
   * The story run, for the fastest-finish ranking: when the first story battle
   * was fought and when the last chapter was completed (ms, null = not yet).
   */
  story: { startedAt: number | null; doneAt: number | null };
  defeatedPresets: string[];
  /** Best star rating (1-3) per preset monster id. */
  monsterStars: Record<string, number>;
  /** Story chapters already completed (reward paid). */
  chaptersDone: string[];
  quests: { claimed: string[] };
  counters: Counters;
  board: BoardState;
  memories: Memory[];
  friends: Friend[];
  settings: Settings;
  online: OnlineIdentity | null;
  savedAt: number;
}

/** The dice board (see systems/board.ts). */
export interface BoardState {
  pos: number;
  rolls: number;
  /** Local day (YYYY-MM-DD) the free rolls were last topped up. */
  day: string;
}

export function newId(prefix: string): string {
  const buf = new Uint32Array(2);
  crypto.getRandomValues(buf);
  return `${prefix}_${(buf[0] ?? 0).toString(36)}${(buf[1] ?? 0).toString(36)}`;
}

export function defaultSettings(): Settings {
  return { volume: 0.7, music: 0.5, muted: false, reducedMotion: 'auto', debug: false, fpsCap: 60, dprCap: 2, renderMode: 'cpu', language: 'auto', fullscreenOnPhone: true, drawMode: 'easy', traceGame: true };
}

export function defaultCounters(): Counters {
  return {
    wins: 0,
    battles: 0,
    monstersCreated: 0,
    bestCombo: 0,
    rockWins: 0,
    paperWins: 0,
    scissorsWins: 0,
    pvpBattles: 0,
    shares: 0,
    presetsDefeated: 0,
    votes: 0,
    hallWins: 0,
    threeStarWins: 0,
    daresSent: 0,
    daresBeaten: 0,
    bossBattles: 0,
    reactionsSent: 0,
    towerBest: 0,
    diceRolls: 0,
    boardLaps: 0,
  };
}

export function newSave(): SaveData {
  return {
    version: SAVE_VERSION,
    ageGroup: null,
    cosmetics: { owned: [], stamp: null },
    contest: { votedRounds: [], prizes: [] },
    community: { bossPrizes: [], classId: null, classPrizes: [], cardSent: '' },
    daily: { day: '', base: defaultCounters(), quests: [], claimed: [], streak: 0, lastFull: null },
    fortune: { tickets: 0, lastFreeDay: null, draws: 0, sinceRare: 0, sinceEpic: 0, sinceLegendary: 0, coinDay: '', coinDraws: 0 },
    heroSlots: 0,
    heroes: [],
    activeHeroId: null,
    battleInProgress: null,
    monsters: [],
    coins: 25,
    items: [],
    itemLevels: {},
    story: { startedAt: null, doneAt: null },
    defeatedPresets: [],
    monsterStars: {},
    chaptersDone: [],
    quests: { claimed: [] },
    counters: defaultCounters(),
    board: { pos: 0, rolls: 5, day: '' },
    memories: [],
    friends: [],
    settings: defaultSettings(),
    online: null,
    savedAt: 0,
  };
}
